import {
  attributeKeys,
  type ContextAttributes,
  mergeAttributes,
  toAttributeRecord,
} from "@/utils/context/attributes";
import { notNull } from "@/utils/helpers";
import { toBaggageEntries } from "@/utils/tracing/baggage";
import { createSpanContext, inject } from "@listen-labs/o11y/tracing";

type TraceScope = { attributes: ContextAttributes };

export type TraceScopeHandle = {
  // Replaces this scope's IDs without changing its position in the stack.
  update: (attributes: ContextAttributes) => void;
  pop: () => void;
};

// Browser counterpart of the server store. One tab is one user on one page,
// so module variables are the whole store.
let base: ContextAttributes = {};
// Mounted scopes, outermost first. Popping one restores whatever the outer
// scopes set, which a flat merge could not do when they share a key.
let scopes: readonly TraceScope[] = [];

const getEffectiveAttributes = (): ContextAttributes =>
  scopes.reduce(
    (merged, scope) => mergeAttributes(merged, scope.attributes),
    base
  );

// Absent keys fall through to the outer scopes, so a nested scope can never
// blank them.
const toOwnedAttributes = (attributes: ContextAttributes): ContextAttributes =>
  Object.fromEntries(
    attributeKeys.flatMap(([key]) => {
      const value = attributes[key];
      return notNull(value) ? [[key, value]] : [];
    })
  );

// Base attributes are independent of mounted scopes; undefined values are ignored.
export const setContext = (attributes: ContextAttributes): void => {
  base = mergeAttributes(base, attributes);
};

export const removeContextEntry = (key: keyof ContextAttributes): void => {
  delete base[key];
};

export const clearContext = (): void => {
  base = {};
};

export const pushTraceScope = (
  attributes: ContextAttributes = {}
): TraceScopeHandle => {
  const scope: TraceScope = { attributes: toOwnedAttributes(attributes) };
  scopes = [...scopes, scope];
  return {
    update: (next) => {
      scope.attributes = toOwnedAttributes(next);
    },
    pop: () => {
      scopes = scopes.filter((candidate) => candidate !== scope);
    },
  };
};

export const getContextAttributes = (): Record<string, string> =>
  toAttributeRecord(getEffectiveAttributes());

// Fresh `traceparent` plus the scope as W3C `baggage` (omitted when empty).
export const getTraceHeaders = (): Record<string, string> =>
  inject({
    spanContext: createSpanContext(),
    baggage: toBaggageEntries(getEffectiveAttributes()),
  });
